import { useNavigate } from "react-router-dom";
import { ArrowRight, Shield, Scale, BookOpen, MessageSquare, LogIn, Upload, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export default function Welcome() {
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-amber-50 to-white">
      {/* Header */}
      <header className="w-full p-4 flex items-center justify-between bg-white/80 backdrop-blur-sm border-b border-orange-100">
        <div className="flex items-center space-x-2">
          <Scale className="h-6 w-6 text-orange-600" />
          <span className="text-xl font-semibold text-orange-900">AdvoKAI</span>
        </div>
        <nav className="hidden md:flex items-center space-x-6 text-orange-600">
          <button onClick={() => handleNavigation('/features')} className="hover:text-orange-700">Funksjoner</button>
          <button onClick={() => handleNavigation('/help')} className="hover:text-orange-700">Slik fungerer det</button>
          <button onClick={() => handleNavigation('/about')} className="hover:text-orange-700">Om oss</button>
        </nav>
        <Button 
          variant="ghost"
          onClick={() => navigate("/login")} 
          className="text-orange-600 hover:text-orange-700"
        >
          <LogIn className="h-4 w-4 mr-2" />
          Logg inn
        </Button>
      </header>

      <section className="flex-1 flex flex-col md:flex-row items-stretch">
        {/* Left side - Hero Content */}
        <div className="w-full md:w-1/2 p-8 flex flex-col justify-center bg-gradient-to-br from-orange-100/50 to-transparent">
          <div className="max-w-xl mx-auto space-y-8">
            <div className="text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-bold text-orange-900 mb-4">
                Din Digitale Juridiske Assistent
              </h1>
              <p className="text-orange-700/80 text-lg mb-8">
                Automatiser sakshåndtering, dokumentgenerering og lovsøk med avansert AI-teknologi
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Button 
                onClick={() => navigate('/login')} 
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2"
              >
                Kom i gang nå
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/about')} 
                className="border-orange-200 hover:bg-orange-100"
              >
                Les mer
                <BookOpen className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Right side - Features */}
        <div className="w-full md:w-1/2 p-8 flex items-center justify-center bg-white/50 backdrop-blur-sm">
          <div className="max-w-xl space-y-6">
            <FeatureCard
              icon={<MessageSquare className="h-6 w-6 text-orange-600" />}
              title="AI-Drevet Juridisk Assistent"
              description="Få øyeblikkelige svar på juridiske spørsmål med vår avanserte RAG AI-teknologi"
            />
            <FeatureCard
              icon={<BookOpen className="h-6 w-6 text-orange-600" />}
              title="Smart Dokumenthåndtering"
              description="Effektiv håndtering og analyse av juridiske dokumenter med AI-støtte"
            />
            <FeatureCard
              icon={<Scale className="h-6 w-6 text-orange-600" />}
              title="Semantisk Lovsøk"
              description="Finn relevant lovverk og prejudikater med vår avanserte søkemotor"
            />
            <FeatureCard
              icon={<Shield className="h-6 w-6 text-orange-600" />}
              title="Sikker og Pålitelig"
              description="Dine data er beskyttet med enterprise-grade sikkerhet og kryptering"
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-orange-50/50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-orange-900 text-center mb-12">
            Slik fungerer det
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <StepCard
              number="1"
              title="Registrer deg"
              description="Opprett en konto og få tilgang til alle funksjoner"
              icon={<LogIn className="h-6 w-6 text-orange-600" />}
              onClick={() => navigate('/login')}
            />
            <StepCard
              number="2"
              title="Last opp dokumenter"
              description="Del dine juridiske dokumenter for analyse"
              icon={<Upload className="h-6 w-6 text-orange-600" />}
              onClick={() => navigate('/documents')}
            />
            <StepCard
              number="3"
              title="Få AI-assistanse"
              description="La vår AI hjelpe deg med analyser og anbefalinger"
              icon={<Bot className="h-6 w-6 text-orange-600" />}
              onClick={() => navigate('/help')}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-orange-100 py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-semibold text-orange-900 mb-4">Om AdvoKAI</h3>
              <ul className="space-y-2 text-orange-600">
                <li><button onClick={() => navigate('/about')} className="hover:text-orange-700">Om oss</button></li>
                <li><button onClick={() => navigate('/contact')} className="hover:text-orange-700">Kontakt</button></li>
                <li><button onClick={() => navigate('/careers')} className="hover:text-orange-700">Karriere</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-orange-900 mb-4">Produkter</h3>
              <ul className="space-y-2 text-orange-600">
                <li><button onClick={() => navigate('/features')} className="hover:text-orange-700">Funksjoner</button></li>
                <li><button onClick={() => navigate('/pricing')} className="hover:text-orange-700">Priser</button></li>
                <li><button onClick={() => navigate('/integrations')} className="hover:text-orange-700">Integrasjoner</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-orange-900 mb-4">Ressurser</h3>
              <ul className="space-y-2 text-orange-600">
                <li><button onClick={() => navigate('/docs')} className="hover:text-orange-700">Dokumentasjon</button></li>
                <li><button onClick={() => navigate('/guides')} className="hover:text-orange-700">Guider</button></li>
                <li><button onClick={() => navigate('/support')} className="hover:text-orange-700">Support</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-orange-900 mb-4">Juridisk</h3>
              <ul className="space-y-2 text-orange-600">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-orange-700">Personvern</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-orange-700">Vilkår</button></li>
                <li><button onClick={() => navigate('/gdpr')} className="hover:text-orange-700">GDPR</button></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-orange-100 text-center text-orange-600">
            <p>© 2024 AdvoKAI. Alle rettigheter forbeholdt.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) {
  return (
    <Card className="p-6 bg-white/80 backdrop-blur-sm border-orange-100/50 hover:bg-white/90 transition-colors">
      <div className="flex items-start space-x-4">
        <div className="shrink-0">
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-orange-900 mb-2">{title}</h3>
          <p className="text-orange-600/80">{description}</p>
        </div>
      </div>
    </Card>
  );
}

function StepCard({ number, title, description, icon, onClick }: { 
  number: string, 
  title: string, 
  description: string,
  icon: React.ReactNode,
  onClick: () => void 
}) {
  return (
    <div 
      className="text-center cursor-pointer hover:transform hover:scale-105 transition-transform duration-200"
      onClick={onClick}
    >
      <div className="w-12 h-12 rounded-full bg-orange-600 text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">
        {number}
      </div>
      <div className="flex justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold text-orange-900 mb-2">{title}</h3>
      <p className="text-orange-600/80">{description}</p>
    </div>
  );
}
